/**
 * Welle 1 (Cockpit-Intelligenz) W1-B/W1-C — FindingResolveControls: GoBD-taugliche
 * "als geklärt markieren" / "ignorieren" / "wieder öffnen" actions for ONE
 * finding.
 *
 * Reference: architecture-cockpit-intelligence.md §GoBD-/Audit-Konzept +
 * §Interfaces (Backend — Resolution-Callables) + modules-cockpit-
 * intelligence.md W1-C. Driven ENTIRELY by the `resolution` already present on
 * the worklist-level `CockpitFinding` (architecture §Datenmodelle #2,
 * `advisorCockpitApi.ts` additive fields) — this component does NOT fetch
 * anything itself (the single `getCockpitDocumentDetail` read for a row lives
 * in the sibling {@link ./FindingDrilldown}). It only calls the WRITE callable
 * `resolveCheckFinding` and reports success upward via `onResolved` so the
 * parent can refetch the worklist (append-only audit — never mutates locally).
 *
 * Wer darf resolven: Owner UND Advisor (server-derived role, `resolveCheckFinding`
 * enforces `assertLoopBBetaScope`). Welle 1's UI surface is StB-only (this
 * component is only mounted inside `AdvisorCockpitPage`).
 *
 * @module components/moat/FindingResolveControls
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSuccess, IconViewOff, IconUndo } from '../../icons';
import { resolveCheckFinding } from '../../api/cockpitResolutionApi';
import type { CockpitFindingResolution } from '../../api/advisorCockpitApi';

export interface FindingResolveControlsProps {
  mandantUid: string;
  docId: string;
  checkId: string;
  /** Current resolution from `item.findings[i].resolution` — `null` ⇒ active. */
  resolution: CockpitFindingResolution | null;
  /** Called after a successful write so the parent can refetch the worklist. */
  onResolved: () => void;
}

type Status = { kind: 'idle' } | { kind: 'saving' } | { kind: 'error' };

/** de-DE date formatting for a millis timestamp. */
function fmtDate(millis: number): string {
  try {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(millis),
    );
  } catch {
    return '–';
  }
}

/**
 * Per-finding resolve/ignore/reopen control, with an optional note field and
 * an audit-trail badge ("geklärt von {Rolle} am {Datum}") once resolved.
 *
 * @param mandantUid the currently selected mandant scope.
 * @param docId the inbound document the finding belongs to.
 * @param checkId the finding's `checkId` (server re-derives the fingerprint).
 * @param resolution the finding's current resolution (or `null`).
 * @param onResolved callback fired after a successful write.
 */
const FindingResolveControls: React.FC<FindingResolveControlsProps> = ({
  mandantUid,
  docId,
  checkId,
  resolution,
  onResolved,
}) => {
  const { t } = useTranslation('common');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const act = async (resolutionType: 'resolved' | 'ignored' | 'reopened'): Promise<void> => {
    setStatus({ kind: 'saving' });
    try {
      await resolveCheckFinding({ mandantUid, docId, checkId, resolutionType, note });
      setNote('');
      setStatus({ kind: 'idle' });
      onResolved();
    } catch {
      setStatus({ kind: 'error' });
    }
  };

  const roleLabel =
    resolution?.resolvedByRole === 'advisor'
      ? t('cockpit.resolve.roleAdvisor', 'Steuerberater')
      : t('cockpit.resolve.roleOwner', 'Mandant');

  if (resolution) {
    const badgeLabel =
      resolution.resolutionType === 'resolved'
        ? t('cockpit.resolve.resolvedBadge', 'Geklärt')
        : t('cockpit.resolve.ignoredBadge', 'Ignoriert');
    const badgeClass =
      resolution.resolutionType === 'resolved'
        ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]'
        : 'bg-gray-600/40 text-secondary';

    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 font-semibold ${badgeClass}`}>
          <IconSuccess size={12} />
          {badgeLabel}
        </span>
        <span className="text-muted">
          {t('cockpit.resolve.resolvedBy', 'von {{role}} am {{date}}', {
            role: roleLabel,
            date: fmtDate(resolution.resolvedAt),
          })}
        </span>
        {resolution.note && <span className="italic text-faint">„{resolution.note}“</span>}
        <button
          type="button"
          onClick={() => act('reopened')}
          disabled={status.kind === 'saving'}
          className="inline-flex items-center gap-1 rounded bg-surface-inset px-2 py-1 font-medium text-secondary transition hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          <IconUndo size={12} />
          {t('cockpit.resolve.reopen', 'Wieder öffnen')}
        </button>
        {status.kind === 'error' && (
          <span className="text-[var(--tone-danger-fg)]">{t('cockpit.resolve.saveError', 'Konnte nicht gespeichert werden.')}</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 text-xs">
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t('cockpit.resolve.notePlaceholder', 'Notiz (optional)')}
        maxLength={1000}
        className="w-full rounded border border-strong bg-surface px-2 py-1 text-secondary focus:border-indigo-400 focus:outline-none"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => act('resolved')}
          disabled={status.kind === 'saving'}
          className="inline-flex items-center gap-1 rounded bg-emerald-700 px-2 py-1 font-medium text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <IconSuccess size={12} />
          {t('cockpit.resolve.markResolved', 'Als geklärt markieren')}
        </button>
        <button
          type="button"
          onClick={() => act('ignored')}
          disabled={status.kind === 'saving'}
          className="inline-flex items-center gap-1 rounded bg-surface-inset px-2 py-1 font-medium text-secondary transition hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          <IconViewOff size={12} />
          {t('cockpit.resolve.markIgnored', 'Ignorieren')}
        </button>
        {status.kind === 'error' && (
          <span className="text-[var(--tone-danger-fg)]">{t('cockpit.resolve.saveError', 'Konnte nicht gespeichert werden.')}</span>
        )}
      </div>
    </div>
  );
};

export default FindingResolveControls;
